import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { Story } from '@/types/cmsTypes';
import type { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import type { SupabaseSceneOrigin } from '@/types/sceneTypes';
import { convertToProjectInstance, convertToSceneOrigin, convertSceneOriginToStory } from '@/utils/dataConverters';
import { useAccountChanges } from '@/modules/mcpStream';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { routeMissing } from '@/infrastructure/api/v1/screenReads';
import { projectToLegacy, type ProjectDto } from '@/infrastructure/api/v1/mappers/projects';
import { storyToLegacy, type StoryDto } from '@/infrastructure/api/v1/mappers/stories';

export type ProjectEditLoadState = 'loading' | 'ready' | 'not-found' | 'error';

const REFRESH_FAILED_TOAST = 'project-edit-refresh-failed';

/**
 * The project and its stories, in one read (getProjectWorkspace), which only the project's owner (or staff) may make;
 * on a backend without that read (rolled back to before it), in two (getProject, listStories), as before it.
 */
async function readProjectAndStories(id: string, userId: string): Promise<V1Result<{ project: ProjectDto; stories: StoryDto[] }>> {
  const workspace = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}/workspace', { params: { path: { projectUniqueId: id } } }));
  if (!routeMissing(workspace.error)) {
    return workspace.error
      ? { data: null, error: workspace.error }
      : { data: { project: workspace.data.project, stories: workspace.data.stories.map(item => item.story) }, error: null };
  }
  const [project, stories] = await Promise.all([
    toResult(v1.GET('/api/v1/projects/{projectUniqueId}', { params: { path: { projectUniqueId: id } } })),
    listAll(page => v1.GET('/api/v1/stories', {
      params: { query: { projectUniqueId: [id], userUniqueId: userId, sort: 'orderNumber', ...page } },
    })),
  ]);
  if (project.error) return { data: null, error: project.error };
  // Someone else's project is not found, whatever its stories' read said
  if (project.data.userUniqueId !== userId) return { data: { project: project.data, stories: [] }, error: null };
  return stories.error ? { data: null, error: stories.error } : { data: { project: project.data, stories: stories.data }, error: null };
}

// Only a missing or someone else's project is "not found"; a failed refresh keeps the page. Stories are read for their locations
export function useProjectEditData(id: string | undefined, userId: string | undefined) {
  const [loadState, setLoadState] = useState<ProjectEditLoadState>('loading');
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [projectInstance, setProjectInstance] = useState<ProjectInstance | null>(null);
  const [projectStories, setProjectStories] = useState<Story[]>([]);
  const seq = useRef(0);
  const shown = useRef(false);
  const deferred = useRef(false);
  const storyIds = useRef(new Set<string>());

  const latestLoad = useRef<() => Promise<void>>(async () => {});

  const load = useCallback(() => {
    if (!id || !userId) return Promise.resolve();
    const mine = ++seq.current;
    const current = () => mine === seq.current;

    return readProjectAndStories(id, userId).then(({ data: workspace, error: failure }) => {
      if (!current()) return;

      const notFound = failure
        ? failure.status === 404 || failure.status === 403
        : workspace.project.userUniqueId !== userId;

      if (notFound) {
        setLoadState('not-found');
        return;
      }
      if (failure) {
        console.error('Error fetching project and stories:', failure);
        if (shown.current) {
          toast.error("We couldn't refresh this project.", { id: REFRESH_FAILED_TOAST, action: { label: 'Retry', onClick: () => void latestLoad.current() } });
        } else {
          setLoadError(failure);
          setLoadState('error');
        }
        return;
      }

      // Legacy rows carry jsonb as JSON text, as the generic route sent it, whatever the Supabase row types say
      setProjectInstance(convertToProjectInstance(projectToLegacy(workspace.project) as unknown as SupabaseProjectInstance));
      const stories = workspace.stories.map((story, index) =>
        convertSceneOriginToStory(convertToSceneOrigin(storyToLegacy(story) as unknown as SupabaseSceneOrigin), index + 1),
      );
      setProjectStories(stories);
      storyIds.current = new Set(stories.map(s => s.id));
      shown.current = true;
      setLoadError(null);
      setLoadState('ready');
      toast.dismiss(REFRESH_FAILED_TOAST);
    });
  }, [id, userId]);

  useLayoutEffect(() => {
    latestLoad.current = load;
  });

  const reload = useCallback(async () => {
    if (!shown.current) setLoadState('loading');
    await load();
  }, [load]);

  // Another project or user starts loading afresh
  const [loadedFor, setLoadedFor] = useState<{ id?: string; userId?: string } | null>(null);
  if (!loadedFor || loadedFor.id !== id || loadedFor.userId !== userId) {
    setLoadedFor({ id, userId });
    setLoadState('loading');
  }

  useEffect(() => {
    shown.current = false;
    void load();
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !deferred.current) return;
      deferred.current = false;
      void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // Changes elsewhere read again in place, once the tab is visible; the form reseeds untouched fields only
  useAccountChanges(['project', 'story'], (events) => {
    if (!shown.current) return;
    const relevant = events.length === 0 || events.some(e =>
      !e.entityUniqueId
      || (e.entity === 'project' && e.entityUniqueId === id)
      || (e.entity === 'story' && (storyIds.current.has(e.entityUniqueId) || e.op === 'created')));
    if (!relevant) return;
    if (document.hidden) deferred.current = true;
    else void load();
  }, 1000);

  return { loadState, loadError, projectInstance, projectStories, reload };
}
